import type { Chat, Message, Person } from '../../components/chats'
import type { Alert } from '../../components/dashboard/queries/alerts'

import _ from 'lodash'
import toast from 'react-hot-toast'

import { ChatType, getChatMessagesQueryKey, getChatsMetaQueryKey, getChatsQueryKey, MessageNotification } from '../../components/chats'
import { SidebarOptionKind } from '../../components/layout/sidebar/types'
import { PATCH_HISTORY } from '../../components/NotificationsHistory/actions'
import { newToasts } from '../../components/shared/toast-stack/actions'
import * as sidebarActions from '../../store/modules/sidebar'
import { queryClient } from '../query-client'
import { getStore } from '../state'

let timeoutId: NodeJS.Timeout

export function handleAlertNotification (notification: Alert, options: { showAlert: boolean }): void {
  const store = getStore()

  if (timeoutId) {
    clearTimeout(timeoutId)
  }

  const prevAlerts = queryClient.getQueryData<Alert[]>([ 'alerts' ])

  timeoutId = setTimeout(() => {
    queryClient.invalidateQueries({ queryKey: [ 'alerts' ] })
      .catch((e) => {
        console.error(e)
      })
  }, 1000)

  if (prevAlerts?.some((a) => a.id === notification.id)) {
    return
  }

  if (options.showAlert) {
    store.dispatch(newToasts([{ ...notification, type: 'alert' }]))
  }
}

interface Payload {
  chat?: Chat | null
  message?: Message | null
  clientName?: string | null
}

interface Participant extends Person {
  resourceType: string
}

export async function handleIncomingMessageNotification (payload: Payload): Promise<void> {
  let store = getStore()
  let state = store.getState()
  let dispatch = store.dispatch

  let chat = payload.chat
  let chatId = payload.chat?.id
  let userId: string | undefined = state?.auth?.profile?.sub

  let participants = (payload.chat?.participants ?? []) as unknown as Participant[]

  let authorId = payload.message?.author ?? ''
  let [ resourceType, id ] = authorId.split('/')
  let author = participants.find(p => p.resourceType === resourceType && p.id === id)
  let isPatient = resourceType === 'Patient'

  if (!chatId || !chat) {
    console.warn('No chat ID found in payload! Not showing notification.')
    return
  }

  if (!author) {
    console.warn('Could not find author of message! Not showing notification.')
    return
  }

  if (userId === author.id) {
    return
  }

  let careTeamMemberId: string = state.sidebar.chats.careTeamMemberId ?? ''

  let isConcierge = !!chat?.meta?.concierge || (
    Array.isArray(participants) &&
      participants.length === 2 &&
      [ `Practitioner/${careTeamMemberId}`, chat?.patient?.id ].every(id => participants.some(p => p.id === id))
  )

  if (payload.message?.concierge && typeof payload.message.concierge === 'object') {
    // @ts-expect-error // It's fine
    author = payload.message.concierge
  }

  await queryClient.invalidateQueries({
    queryKey: getChatsQueryKey({
      userId
    }),
    exact: false
  })

  const queries = []

  queries.push(
    queryClient.invalidateQueries({
      queryKey: [
        'chat',
        { id: chatId, userId }
      ],
      exact: false
    })
  )

  queries.push(
    queryClient.invalidateQueries({
      queryKey: getChatMessagesQueryKey({
        userId,
        chatId
      }),
      exact: false
    })
  )

  if (state.sidebar.chats.selectedChatId !== payload.chat?.id) {
    queries.push(
      queryClient.invalidateQueries({
        queryKey: getChatsMetaQueryKey({
          userId
        }),
        exact: false
      })
    )
  }

  Promise.all(queries)
    .then(() => {
      if (chatId) {
        void queryClient.invalidateQueries({
          queryKey: [ 'chat', { id: chatId, userId }],
          exact: false
        })
      }
    })
    .catch((e) => {
      console.error(e)
    })

  if (state.sidebar.chats.selectedChatId === payload.chat?.id) {
    return
  }

  const { status } = state?.auth ?? {}
  const statuses = _.get(status, 'data', [])

  const isUserConcierge = _.get(
    _.find(statuses, s => s.id === userId),
    'concierge',
    false
  )

  const isCareTeamChat = payload.chat?.meta?.concierge

  if (isCareTeamChat && !isUserConcierge) {
    return
  }

  let preview = payload.message?.attachments?.[0]?.preview
  let date = payload.message?.created_at ?? undefined

  let attachment = typeof preview === 'string' ? { preview } : undefined
  let message = payload.message?.body

  store.dispatch({
    type: PATCH_HISTORY,
    notifications: [{
      payload,
      type: 'chat_message'
    }]
  })

  toast.custom(t => (
    <MessageNotification
      {...t}

      // key={notificationId}
      author={author as NonNullable<typeof author>}
      attachment={attachment}
      date={typeof date === 'string' ? new Date(date) : undefined}
      isPatient={isPatient}
      onDismiss={() => {
        toast.dismiss(t.id)
      }}
      onOpen={() => {
        toast.dismiss(t.id)

        let chat = payload.chat
        let chatId = chat?.id

        if (typeof chatId !== 'string' || !chat) {
          return
        }

        dispatch(sidebarActions.open(SidebarOptionKind.messages))
        dispatch(sidebarActions.selectTab(isConcierge ? ChatType.Concierge : ChatType.Own))
        dispatch(sidebarActions.selectChat(chatId))
      }}
    >
      {typeof message === 'string' && message?.trim()?.length > 0 ? message : payload.message?.attachments?.length ? 'Attachment' : 'N/A'}
    </MessageNotification>
  ), {
    position: 'top-right',
    duration: 7000
  })
}
